import {
  KernelEstimationFunction,
  getKernelEsimationFunction,
  kde,
  kurt,
  kurt_def,
  skew_def,
} from "@features/results/Result/const"
import { ChartOptions, Scale } from "chart.js"
import { AnnotationOptions } from "chartjs-plugin-annotation"
import { ZoomPluginOptions } from "chartjs-plugin-zoom/types/options"
import * as d3 from "d3"

export type ChartOptionsSettings = {
  showEstimations: boolean
  maintainAspectRatio: boolean
  onZoomChange: ({ xMin, xMax }: { xMin: number; xMax: number }) => void
  annotations: Record<string, AnnotationOptions>
  ticksCallback: (this: Scale, value: string | number) => string
  legendPosition?: "top" | "bottom"
  showLegend?: boolean
  onLegendClick?: (datasetLabel: string) => void
}

const getChartZoomOptions = (
  onChange: ({ xMin, xMax }: { xMin: number; xMax: number }) => void,
): ZoomPluginOptions => {
  return {
    limits: { x: { minRange: 1 } },
    zoom: {
      drag: { enabled: true },
      pinch: { enabled: true },
      mode: "x",
      onZoomComplete: ({ chart }) => {
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        const xMin = chart.scales.x.getLabelForValue(chart.scales.x.min)
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        const xMax = chart.scales.x.getLabelForValue(chart.scales.x.max)

        onChange({ xMax: Number(xMax), xMin: Number(xMin) })
      },
    },
  }
}

export const getChartOptions = (settings: ChartOptionsSettings): ChartOptions<"line"> => {
  return {
    responsive: true,
    maintainAspectRatio: settings.maintainAspectRatio,
    layout: { padding: { top: settings.showEstimations ? 60 : 0 } },
    plugins: {
      zoom: getChartZoomOptions(settings.onZoomChange),
      annotation: {
        clip: false,
        common: { drawTime: "afterDraw" },
        annotations: settings.showEstimations ? settings.annotations : undefined,
      },
      legend: {
        position: settings.legendPosition,
        display: settings.showLegend ?? true,
        labels: {
          filter: (item) => Boolean(item.text),
          boxWidth: 11,
          boxHeight: 11,
          font: {
            size: 11,
          },
        },
        onClick: settings.onLegendClick
          ? function (event, legendItem, legend) {
              const index = legendItem.datasetIndex
              if (index === undefined) return
              const dataset = legend.chart.data.datasets[index]
              if (dataset?.label) {
                settings.onLegendClick!(dataset.label)
              }
            }
          : undefined,
      },
      title: { display: false },
      filler: { propagate: false },
    },
    animation: { duration: 200 },
    elements: { point: { radius: 0 } },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          callback: settings.ticksCallback,
        },
      },
    },
  }
}

export const pos = (adjustedValues: number[][], xValues: string[], count = 0) => {
  // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
  const meanval = d3.mean(adjustedValues[0]) || 0
  // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
  const stdev = d3.deviation(adjustedValues[0]) || 0
  const maxVal = d3.max(xValues.map((x) => parseFloat(x))) || 0
  const minVal = d3.min(xValues.map((x) => parseFloat(x))) || 0

  // real space range
  const range = xValues.length
  const dataRange = maxVal - minVal //11.66 - 4.07=7.59
  const percentageInDataRange = (meanval + stdev * count - minVal) / dataRange //7.55-4.07/7.59
  return range * percentageInDataRange
}

export const getStandardAnnotations = (adjustedValues: number[][], xValues: string[]) => {
  return {
    stdev_3lower: getDevisionLower3Annotation(adjustedValues, xValues),
    stdev_2lower: getDevisionLower2Annotation(adjustedValues, xValues),
    stdev_lower: getDevisionLowerAnnotation(adjustedValues, xValues),
    mean: getMeanAnnotation(adjustedValues, xValues),
    stdev_upper: getDevisionUpperAnnotation(adjustedValues, xValues),
    stdev_2upper: getDevisionUpper2Annotation(adjustedValues, xValues),
    stdev_3upper: getDevisionUpper3Annotation(adjustedValues, xValues),
  }
}

export const getClampPercentageAnnotation = (
  adjustedValues: number[][],
  values: { x: string[]; y: number[] },
  percentageAreaUnderCurve: string,
): AnnotationOptions<"label"> => {
  return {
    type: "label",
    xValue: pos(adjustedValues, values.x, 0),
    yValue: (d3.max(values.y) || 0) / 1.1,
    backgroundColor: "rgba(245,245,245,0.0)",
    content: ["Area under curve", percentageAreaUnderCurve],
    font: { size: 16 },
  }
}

export const getAnnotations = (
  adjustedValues: number[][],
  values: { x: string[]; y: number[] },
  clamp: boolean,
  estimationBandwidth: number,
  clampCallback: (i: number) => boolean,
  kernelFunction?: KernelEstimationFunction,
): Record<string, AnnotationOptions> => {
  const standardAnnotations = getStandardAnnotations(adjustedValues, values.x)

  const clampEstimationFunction = getKernelEsimationFunction(kernelFunction)
  if (!clamp || !clampEstimationFunction) return standardAnnotations

  // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
  const binWidth = +values.x[1] - +values.x[0]

  const allPnSelected = kde(
    values.x,
    // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
    adjustedValues[0],
    clampEstimationFunction,
    estimationBandwidth,
    binWidth,
    clampCallback,
  )
  const percentageUnderCurve = 100 * Math.min(1.0, d3.sum(allPnSelected) * binWidth)

  const clampAnnotation = getClampPercentageAnnotation(
    adjustedValues,
    values,
    `${percentageUnderCurve.toFixed(2)}%`,
  )

  return {
    ...standardAnnotations,
    clamp_percentage: clampAnnotation,
  }
}

export const getClampCallback = (
  adjustedValues: number[][],
  xValues: string[],
  clamp: boolean,
  clampStDev: number,
): ((i: number) => boolean) => {
  return (i: number) =>
    !clamp ||
    (i > pos(adjustedValues, xValues, -clampStDev) && i < pos(adjustedValues, xValues, clampStDev))
}

const getMeanAnnotation = (
  adjustedValues: number[][],
  xValues: string[],
): AnnotationOptions<"line"> => {
  // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
  const meanValue = d3.mean(adjustedValues[0]) || 0
  // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
  const medianValue = d3.median(adjustedValues[0]) || 0

  // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
  const stdev = d3.deviation(adjustedValues[0]) || 0
  const skew = ((3 * (meanValue - medianValue)) / stdev)?.toFixed(4)
  // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
  const tails = kurt(meanValue, adjustedValues[0])?.toFixed(4)

  return {
    type: "line",
    borderColor: "rgba(102, 102, 102, 0.5)",
    borderDash: [3, 3],
    borderDashOffset: 0,
    borderWidth: 1,
    label: {
      display: true,
      backgroundColor: "rgba(102, 102, 102, 0.5)",
      color: "white",
      shadowBlur: 1,
      borderJoinStyle: "round",
      borderWidth: 0,
      // radius: 10,
      // zIndex: 200,
      content: () => [
        `μ=${meanValue?.toFixed(2)}  skew=${skew} ${skew_def(+skew)}`,
        `kurtosis=${tails} ${kurt_def(+tails)}`,
      ],
      yAdjust: -85,
      position: "start",
      rotation: 0,
    },
    //   font: { size: 14 },
    scaleID: "x",
    value: () => pos(adjustedValues, xValues, 0),
  }
}

const getDevisionUpperAnnotation = (
  adjustedValues: number[][],
  xValues: string[],
): AnnotationOptions<"line"> => {
  // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
  const meanValue = d3.mean(adjustedValues[0]) || 0
  // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
  const stdev = d3.deviation(adjustedValues[0]) || 0

  return {
    type: "line",
    borderColor: "rgba(102, 102, 102, 0.5)",
    borderDash: [6, 6],
    borderDashOffset: 0,
    borderWidth: 1,
    label: {
      display: true,
      backgroundColor: "rgba(102, 102, 102, 0.0)",
      color: "gray",
      content: () => "μ+σ = " + (meanValue + stdev).toFixed(2),
      position: "start",
      rotation: 90,
    },
    scaleID: "x",
    //   font: { size: 14 },
    value: () => pos(adjustedValues, xValues, 1),
  }
}

const getDevisionUpper2Annotation = (
  adjustedValues: number[][],
  xValues: string[],
): AnnotationOptions<"line"> => {
  // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
  const meanValue = d3.mean(adjustedValues[0]) || 0
  // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
  const stdev = d3.deviation(adjustedValues[0]) || 0

  return {
    type: "line",
    borderColor: "rgba(102, 102, 102, 0.5)",
    borderDash: [12, 12],
    borderDashOffset: 0,
    borderWidth: 1,
    label: {
      display: true,
      backgroundColor: "rgba(102, 102, 102, 0.0)",
      color: "gray",
      content: () => "μ+2σ = " + (meanValue + 2 * stdev).toFixed(2),
      position: "start",
      rotation: 90,
    },
    scaleID: "x",
    //   font: { size: 14 },
    value: () => pos(adjustedValues, xValues, 2),
  }
}

const getDevisionUpper3Annotation = (
  adjustedValues: number[][],
  xValues: string[],
): AnnotationOptions<"line"> => {
  // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
  const meanValue = d3.mean(adjustedValues[0]) || 0
  // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
  const stdev = d3.deviation(adjustedValues[0]) || 0

  return {
    type: "line",
    borderColor: "rgba(102, 102, 102, 0.5)",
    borderDash: [12, 12],
    borderDashOffset: 0,
    borderWidth: 1,
    label: {
      display: true,
      backgroundColor: "rgba(102, 102, 102, 0.0)",
      color: "gray",
      content: () => "μ+3σ = " + (meanValue + 3 * stdev).toFixed(2),
      position: "start",
      rotation: 90,
    },
    //   font: { size: 14 },
    scaleID: "x",
    value: () => pos(adjustedValues, xValues, 3),
  }
}

const getDevisionLowerAnnotation = (
  adjustedValues: number[][],
  xValues: string[],
): AnnotationOptions<"line"> => {
  // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
  const meanValue = d3.mean(adjustedValues[0]) || 0
  // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
  const stdev = d3.deviation(adjustedValues[0]) || 0

  return {
    type: "line",
    borderColor: "rgba(102, 102, 102, 0.5)",
    borderDash: [6, 6],
    borderDashOffset: 0,
    borderWidth: 1,
    label: {
      display: true,
      backgroundColor: "rgba(102, 102, 102, 0.0)",
      color: "gray",
      content: () => "μ-σ = " + (meanValue - stdev).toFixed(2),
      position: "start",
      rotation: 90,
    },
    scaleID: "x",
    value: () => pos(adjustedValues, xValues, -1),
    //   font: { size: 14 },
  }
}

const getDevisionLower2Annotation = (
  adjustedValues: number[][],
  xValues: string[],
): AnnotationOptions<"line"> => {
  // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
  const meanValue = d3.mean(adjustedValues[0]) || 0
  // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
  const stdev = d3.deviation(adjustedValues[0]) || 0

  return {
    type: "line",
    borderColor: "rgba(102, 102, 102, 0.5)",
    borderDash: [12, 12],
    borderDashOffset: 0,
    borderWidth: 1,
    label: {
      display: true,
      backgroundColor: "rgba(102, 102, 102, 0.0)",
      color: "gray",
      content: () => "μ-2σ = " + (meanValue - 2 * stdev).toFixed(2),
      position: "start",
      rotation: 90,
    },
    scaleID: "x",
    value: () => pos(adjustedValues, xValues, -2),
    //   font: { size: 14 },
  }
}

const getDevisionLower3Annotation = (
  adjustedValues: number[][],
  xValues: string[],
): AnnotationOptions<"line"> => {
  // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
  const meanValue = d3.mean(adjustedValues[0]) || 0
  // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
  const stdev = d3.deviation(adjustedValues[0]) || 0

  return {
    type: "line",
    borderColor: "rgba(102, 102, 102, 0.5)",
    borderDash: [12, 12],
    borderDashOffset: 0,
    borderWidth: 1,
    label: {
      display: true,
      backgroundColor: "rgba(102, 102, 102, 0.0)",
      color: "gray",
      content: () => "μ-3σ = " + (meanValue - 3 * stdev).toFixed(2),
      position: "start",
      rotation: 90,
    },
    scaleID: "x",
    //   font: { size: 14 },
    value: () => pos(adjustedValues, xValues, -3),
  }
}
